import React, { useState, useRef, useEffect } from 'react'
import useField from 'components/Form/useField'
import * as validations from 'components/Form/validations'
import Hovercard from 'components/layout/Hovercard'
import AddressList from 'components/Form/AddressList'
import styled, { css } from 'styled-components/macro'
import { body1, caption } from 'styles/mixins/textstyles'
import uniqueId from 'lodash/uniqueId'

import Modal from 'components/layout/Modal'
import ValidateAddress from 'components/Form/ValidateAddress'

const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)

const Container = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: .2rem;
  border-bottom: 1px solid ${props => {
    if (props.error) {
      return props.theme.colors.errorBase
    } else if (props.focused) {
      return props.theme.colors.primaryBase
    } else {
      return props.theme.colors.greyLight
    }
  }};
`

const FieldStyle = css`
  ${props => body1(props.theme.colors.black)}
  width: 100%;
  border: none;
  border-radius: 0;
  padding: 0;
  background: none;
  line-height: 2.2rem;

  ::placeholder {
    color: ${props => props.theme.colors.greyBase};
  }

  :focus {
    outline: none;

    ::placeholder {
      color: ${props => props.theme.colors.primaryBase};
    }
  }

  &[disabled] {
    color: ${props => iOS ? props.theme.colors.black : props.theme.colors.greyLight};
  }
`

const Input = styled.input`
  ${FieldStyle}
`

const LabelText = styled.label`
  ${props => caption(props.theme.colors.black, 'medium')}
  display: block;
  text-align: left;
  color: ${props => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: ${props => props.tight ? '.2rem' : '1rem'};
  padding-bottom: .1rem;

  ${props => props.focused && css`
    color: ${props => props.theme.colors.primaryBase};
  `}

  ${props => props.error && css`
    color: ${props => props.theme.colors.errorBase};
  `}
`

const Label = props => {
  const { label, error, value, focused, tight, id, } = props
  const text = error || (value && label) || '\u00A0'
  return <LabelText focused={focused} error={error} tight={tight} id={id}>{text}</LabelText>
}

const invalid = validations.makeValidation(() => {
  return validations.FAILED
}, 'Please choose from the list')

const getValidationGroup = (base, valid) => {
  if (base && !valid) {
    return base.and(invalid)
  } else if (!valid) {
    return invalid
  } else {
    return base
  }
}

const TextField = props => {
  const { name, validate: baseValidate, defaultValue, activeLabel, label, addressType, ...rest } = props
  const [ focused, setFocused ] = useState(false)
  const [ valid, setValid ] = useState(!!defaultValue)
  const [showManualAddressModal, setShowManualAddressModal] = useState(false)

  const validate = getValidationGroup(baseValidate, valid)

  const { value, setValue, error, revalidate, id } = useField({ name, validate, defaultValue })
  const floatingLabel = !!value && (activeLabel || label)
  const containerRef = useRef()
  const inputRef = useRef()
  
  useEffect(() => {
    const handler = e => {
      if (inputRef && !inputRef.current.contains(e.target)) {
        setFocused(false)
      }
    }
    const listen = () => window.addEventListener('click', handler)
    const unlisten = () => window.removeEventListener('click', handler)

    listen()

    return () => {
      unlisten()
    }
  }, [inputRef])

  const handleChange = event => {
    if (!focused) setFocused(true)
    setValue(event.target.value)
    setValid(false)
    revalidate()

    if (rest.onChange) {
      event.persist()
      rest.onChange(event)
    }
  }

  const handleFocus = event => {
    setFocused(true)
    if (rest.onFocus) {
      event.persist()
      rest.onFocus(event)
    }
  }

  const handleBlur = event => {
    revalidate()
    if (rest.onBlur) {
      event.persist()
      rest.onBlur(event)
    }
  }

  const handleSelect = address => {
    props.onaddress(address)
  }

  return (
    <>
      <Container focused={focused} error={error} ref={containerRef}>
        <Label
          id={`${id}-label`}
          focused={focused}
          label={floatingLabel}
          value={value}
          error={error}
          tight={props.tight}
        />
        <Input
          id={id}
          {...rest}
          placeholder={label || rest.placeholder}
          value={value}
          error={error}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          ref={inputRef}
        />
      </Container>
      {value && focused && !valid && (
        <Hovercard dockBelow={containerRef.current}>
          <AddressList
            addressType={addressType}
            input={value}
            onSelect={handleSelect}
            onManual={() => setShowManualAddressModal(true)}
          />
        </Hovercard>
      )}
      {showManualAddressModal && (
        <Modal.Basic title="Address Entry" onRequestClose={() => setShowManualAddressModal(false)}>
          <ValidateAddress onValidAddress={handleSelect} />
        </Modal.Basic>
      )}
    </>
  )
}

const Wrapper = props => {
  const { onAddress, displayField = 'text', defaultValue: startValue, ...rest } = props
  const [defaultValue, setDefaultValue] = useState(startValue)
  const [key, setKey] = useState(defaultValue || 'empty')
  useEffect(() => {
    setDefaultValue(startValue)
    setKey(`${startValue}-${uniqueId()}`)
  }, [startValue])
  const handleSelect = address => {
    setDefaultValue(address[displayField])
    setKey(`${startValue}-${uniqueId()}`)
    if (onAddress) {
      onAddress(address)
    }
  }
  // console.log('key', key)
  return <TextField key={key} {...rest} defaultValue={defaultValue} onaddress={handleSelect} />
}

export default Wrapper